import {
  animate,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AutodrawDialogComponent } from '@components/dialog/autodraw-dialog/autodraw-dialog.component';
import { InstallAppDialogComponent } from '@components/dialog/install-app-dialog/install-app-dialog.component';
import { DialogWorldwideComponent } from '@components/dialog/worldwide-dialog/worldwide-dialog.component';
import { ItemTab } from '@components/sticky-nav/sticky-nav.component';
import { ProductPageData } from '@core/constants/product-instant-data';
import { Cart, CartItem } from '@core/models/cart.model';
import { GeoCountry, GeoData } from '@core/models/country.model';
import { FaqItem } from '@core/models/faq.model';
import { ProductTiketsForm } from '@core/models/form-interfaces';
import { HeaderAlert } from '@core/models/header-alert.model';
import { MediaInfo } from '@core/models/media-info.model';
import {
  RaffleInstantBadgePageType,
  RaffleInstantType
} from '@core/models/raffle-instant-type.enum';
import {
  DrawAvailability,
  InstantWinGroup,
  Raffle
} from '@core/models/raffle.model';
import {
  TrustpilotConfig,
  TrustpilotTemplate,
  TrustpilotTheme
} from '@core/models/trustpilot.model';
import { AbTestsService } from '@core/services/ab-tests/ab-tests.service';
import { CartService } from '@core/services/cart/cart.service';
import { CountriesService } from '@core/services/countries/countries.service';
import { DateFormatterService } from '@core/services/date-formatter/date-formatter.service';
import { DateParserService } from '@core/services/date-parser/date-parser.service';
import { ErrorProcessorService } from '@core/services/error-processor/error-processor.service';
import { GonativeService } from '@core/services/gonative/gonative.service';
import { GtmService } from '@core/services/gtm/gtm.service';
import { MetaTagsService } from '@core/services/meta-tags/meta-tags.service';
import { ProjectDataService } from '@core/services/project-data/project-data.service';
import { UserengageService } from '@core/services/userengage/userengage.service';
import { API_LIST } from '@core/services/web-api/api-list';
import { Form } from '@core/type/form.type';
import { Select } from '@ngxs/store';
import { ProductDialogComponent } from '@shared/product-page/components/product-dialog/product-dialog.component';
import { AuthState } from '@store/auth/auth.state';
import { CartState, CartStateModel } from '@store/cart/cart.state';
import { Observable, Subject, fromEvent } from 'rxjs';
import { delay, takeUntil } from 'rxjs/operators';

export enum EntryOption {
  Online = 'online',
  Free = 'free'
}

@Component({
  selector: 'app-shared-product',
  templateUrl: './product.component.html',
  styleUrls: ['./product.component.scss'],
  animations: [
    trigger('showLess', [
      state('in', style({ transform: 'translateY(100%)' })),
      state('out', style({ transform: 'translateY(0)' })),
      transition('in <=> out', animate('250ms ease'))
    ])
  ]
})
export class ProductComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('scrollbarMeasure') scrollbarMeasure: ElementRef;
  @ViewChild('mobileStickyTriggerElement')
  mobileStickyTriggerElement: ElementRef;

  readonly entryOption = EntryOption;
  readonly raffleInstantType = RaffleInstantType;
  readonly groupLimit = 100;
  readonly raffleInstantBadgePageType = RaffleInstantBadgePageType;

  public productPageText: { [key: string]: string };
  public formGroup: FormGroup<Form<ProductTiketsForm>>;
  public product: Raffle;
  public faqItems: FaqItem[];
  public instantWinGroup: InstantWinGroup[];
  public min = 1;
  public instantWinGroupLimit = this.groupLimit;
  public showLessInstantWinGroup = false;
  public stickyLessInstantWinGroup = false;
  public isActiveLessBtn = false;
  public closeAllCard = false;
  public sliderMinTickets = 1;
  public modalShown = false;
  public errorMessage: string | null = null;
  public errorMessageTop: string | null = null;
  public isShowSuccessMessage = false;
  public isLoading = true;
  public isLoadingInstantWinGroup = true;
  public message = '';
  public _alive$ = new Subject<void>();
  public galleryCoverText: string | null = null;
  public isAddingToCart = false;
  public addedTickets = 0;
  public sliderMaxTickets = 100;
  public hasError404: boolean;
  public isTooMuchTicketPerUser: boolean;
  public sliderTicketQuantity: number;
  public cartItemQuantity = 0;
  public currentUrl = '';
  public activeEntryOption = EntryOption.Online;
  public shareOptions = ['messenger', 'whatsapp', 'copy'];
  public campaign: string | null = '()';
  public showDiscountTimer = true;
  public showCountdown = true;
  public discountTimerString = '';
  public sliderValue = 0;
  public drawTimeVersion = '';
  public triedSubmitTickets = false;
  public geoVersion: GeoCountry = '';
  public entranceAvailable = true;
  public openBrowserMode = false;
  public installAppMode = false;
  public queryParams: any;
  public closedRaffleInstantWinMessage = 'This competition has now closed.';
  public handleSubmitIterations = 0;
  public facebookUrl = '';
  public youtubeUrl = '';
  public sectionsNavItems: ItemTab[] = [
    {
      id: 3,
      idAttr: 'competition-details-section',
      fullName: 'Competition Details',
      name: 'Competition Details'
    },
    {
      id: 4,
      idAttr: 'faq-section',
      fullName: 'FAQ',
      name: 'FAQ'
    }
  ];
  public sectionsNavButtons: ItemTab[] = [];
  public projectCode = 'default';
  public trustpilotConfig: TrustpilotConfig = {
    template: TrustpilotTemplate.Free,
    theme: TrustpilotTheme.Light
  };
  public headerAlertData: HeaderAlert | null = null;
  public showMobileStickyBar: boolean = false;
  public isNativeAppVisible = false;
  public videoUrl: string = null;
  public videoId: string = null;

  @Select(CartState.state) cart$: Observable<CartStateModel>;
  @Select(AuthState.hasBoughtIT) hasBoughtIT$: Observable<string[]>;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private formBuilder: NonNullableFormBuilder,
    private route: ActivatedRoute,
    private cartService: CartService,
    private window: Window,
    private userEngage: UserengageService,
    private metaTagsService: MetaTagsService,
    private gtmService: GtmService,
    private httpClient: HttpClient,
    public countriesService: CountriesService,
    public dialog: MatDialog,
    private projectDataService: ProjectDataService,
    private abTests: AbTestsService,
    private zone: NgZone,
    private router: Router
  ) {
    this.projectCode = this.projectDataService.get('project');
    this.trustpilotConfig.template = TrustpilotTemplate.Default;
    this.facebookUrl = this.projectDataService.get('facebook_url');
    this.youtubeUrl = this.projectDataService.get('youtube_url');
  }

  get getProgress(): string {
    return (
      (this.product?.current_entries * 100) / this.product?.max_entries + '%'
    );
  }

  get galleryProductItems(): MediaInfo[] {
    return [this.product.thumbnail, ...this.product.item_photos];
  }

  get oldPrice(): string {
    return this.product.offers[0].original_price_value_frontend;
  }

  get price() {
    return this.product.offers[0].price;
  }

  get mixedDrawDateDiff(): boolean {
    return (
      DateFormatterService.getDateDiff(
        this.product.end_at,
        this.product.result_at,
        'mins'
      ) > 35 && this.product.is_open
    );
  }

  get isMixedDraw(): boolean {
    return this.product.instant_win_count > 0 && this.product.prize_count > 0;
  }

  get badgeDate(): string {
    return this.isMixedDraw
      ? this.mixedDrawDateDiff
        ? this.product.end_at
        : this.product.result_at
      : this.product.result_at;
  }

  get badgeLabel(): string {
    return this.product.is_without_draw ||
      (this.isMixedDraw && this.mixedDrawDateDiff)
      ? 'Closes'
      : 'Draw';
  }

  get currency() {
    return this.product.offers[0].currency;
  }

  get faqList(): FaqItem[] {
    return this.product.faq_group ? this.product.faq_group.items : [];
  }

  get isInstantWin(): boolean {
    return (
      this.product.instant_win_count > 0 &&
      this.product.category_ids.includes('instant-wins')
    );
  }

  ngOnInit(): void {
    this.isNativeAppVisible = GonativeService.isGonative();
    this.campaign = window['gtmTrackedCampaignName'];

    this.currentUrl = window.location.origin + window.location.pathname;

    this.getQueryParams();

    this.route.params.pipe(takeUntil(this._alive$)).subscribe({
      next: () => {
        this.resetCompetitionData();
        this.initProductData();
        this.listenProductItemChange();
      }
    });

    this.countriesService.visitorCountry$.subscribe({
      next: (geoData: GeoData) => {
        if (geoData) {
          this.geoVersion = this.countriesService.getGeoVersionByCountryIso(
            geoData.country_iso,
            this.route
          );
        }
      }
    });

    this.onResize();
  }

  ngAfterViewInit() {
    this.zone.runOutsideAngular(() => {
      window.addEventListener('scroll', this.onWindowScroll.bind(this));
    });
  }

  ngOnDestroy(): void {
    this._alive$.next();
    this._alive$.complete();
    window.removeEventListener('scroll', this.onWindowScroll.bind(this));
  }

  ngOnDetach() {
    this.metaTagsService.resetTags([
      'og:type',
      'og:title',
      'og:image',
      'og:url',
      'og:description',
      'product:price:amount',
      'product:price:currency',
      'product:availability',
      'product:condition',
      'product:retailer_item_id',
      'product:category'
    ]);
    this.metaTagsService.resetTitle();
  }

  setMetaTags(product: Raffle) {
    if (!window['ENVIRONMENT_PARAMETERS']['facebook_update_catalogue']) {
      return;
    }

    const firstOffer = product.offers[0];

    this.metaTagsService.setMeta({ property: 'og:type', content: 'product' });
    this.metaTagsService.setMeta({
      property: 'og:title',
      content: product.title
    });
    this.metaTagsService.setMeta({
      property: 'og:image',
      content: product.thumbnail.url
    });
    this.metaTagsService.setMeta({
      property: 'og:url',
      content:
        window.location.origin + '/product/' + product.slug + '?from=fb-cat'
    });
    this.metaTagsService.setMeta({
      property: 'og:description',
      content: product.title
    });
    this.metaTagsService.setMeta({
      property: 'product:price:amount',
      content: firstOffer.price
    });
    this.metaTagsService.setMeta({
      property: 'product:price:currency',
      content: firstOffer.currency
    });
    this.metaTagsService.setMeta({
      property: 'product:availability',
      content: 'in stock'
    });
    this.metaTagsService.setMeta({
      property: 'product:condition',
      content: 'new'
    });
    this.metaTagsService.setMeta({
      property: 'product:retailer_item_id',
      content: String(product.id)
    });
    this.metaTagsService.setMeta({
      property: 'product:category',
      content: '5032'
    }); // https://www.google.com/basepages/producttype/taxonomy-with-ids.en-US.txt
    this.metaTagsService.setTitle(product.title);
  }

  ngOnAttach(): void {
    if (this.product) {
      this.setMetaTags(this.product);
    }
  }

  initDiscountTimer(): void {
    this.showDiscountTimer = false;
    const mainOffer = this.product.offers[0];

    if (
      !this.product.discount_end_at ||
      !mainOffer.original_price_value_frontend
    ) {
      return;
    }

    this.cycleDiscountTimer();
    this.showDiscountTimer = true;
  }

  cycleDiscountTimer(): void {
    const endAtDate = DateParserService.parse(this.product.discount_end_at);

    if (!endAtDate) {
      this.showDiscountTimer = false;
      return;
    }

    const endAtTime = Math.round(endAtDate.getTime() / 1000);
    let timeLeft = endAtTime - Math.round(new Date().getTime() / 1000);

    if (timeLeft < 0) {
      this.showDiscountTimer = false;
      return;
    }

    const d = Math.floor(timeLeft / 86400);
    timeLeft -= d * 86400;
    const h = Math.floor(timeLeft / 3600);
    timeLeft -= h * 3600;
    const m = Math.floor(timeLeft / 60);
    timeLeft -= m * 60;
    const s = timeLeft;

    let timerString = '';

    if (d > 0) {
      timerString += d + 'd ';
    }

    if (h > 0 || timerString) {
      timerString += (h < 10 ? '0' : '') + h + 'h ';
    }

    if (m > 0 || timerString) {
      timerString += (m < 10 ? '0' : '') + m + 'm ';
    }

    if (s > 0 || timerString) {
      timerString += (s < 10 ? '0' : '') + s + 's ';
    }

    this.discountTimerString = timerString;

    setTimeout(() => {
      this.cycleDiscountTimer();
    }, 333);
  }

  initProductData(): void {
    const slug = this.route.snapshot.params.slug;
    const cartId = localStorage.getItem('cart.id')
      ? JSON.parse(localStorage.getItem('cart.id'))
      : null;

    this.httpClient
      .get(API_LIST.getGameBySlug(slug, 'GBP', cartId, true))
      .subscribe({
        next: (response: Raffle) => {
          this.product = response;
          window['RAFFLE_ID'] = response.id;
          this.hasError404 = false;
          this.isLoading = false;
          this.productPageText = this.isInstantWin
            ? ProductPageData[this.product.instant_win_type]
            : ProductPageData['default'];

          //this.abTests.routeTest('item-page-pilot', 4);
          this.abTests.sendEvent('item-page', true, String(this.product.id));

          const isInApp = GonativeService.isGonative();

          if (
            !this.product.end_at ||
            !DateParserService.parse(this.product.end_at) ||
            this.projectCode !== 'ukcarp'
          ) {
            this.showCountdown = false;
          }

          if (
            this.product.purchase_available_type === DrawAvailability.AppOnly &&
            !isInApp
          ) {
            this.installAppMode = true;
          } else if (
            this.product.purchase_available_type ===
              DrawAvailability.BrowserOnly &&
            isInApp
          ) {
            this.openBrowserMode = true;
          }

          if (this.product.is_open) {
            this.sectionsNavButtons.push({
              id: 1,
              idAttr: 'product-tabs-section',
              fullName: this.productPageText.sectionsNavButton,
              name: this.productPageText.sectionsNavButton,
              icon: 'ticket-small'
            });
          }

          if (this.product.instant_win_count > 0) {
            this.getInstantWinGroups(slug);
            this.closedRaffleInstantWinMessage =
              'This competition has now closed. All prizes have been won.';
            const name = this.productPageText.sectionsNavItemsPrize;
            this.sectionsNavItems.push({
              id: 2,
              idAttr: 'instant-win-section',
              fullName: name,
              name
            });
            this.installPageScrollListener();
          } else {
            this.closedRaffleInstantWinMessage =
              'This competition has sold out.';
          }

          this.entranceAvailable =
            !this.installAppMode && !this.openBrowserMode;

          let defaultTickets =
            this.product.default_tickets > this.product.ticket_limit_per_user &&
            this.product.ticket_limit_per_user
              ? this.product.ticket_limit_per_user
              : this.product.default_tickets;

          if (!defaultTickets) {
            defaultTickets = 1;
          }

          this.formGroup.get('tickets').setValue(defaultTickets);

          this.drawTimeVersion = DateFormatterService.drawRelativeTimeVersion(
            this.product.result_at
          );

          this.sliderValue = Math.floor(
            (this.product.current_entries / this.product.max_entries) * 100
          );

          if (this.product.ticket_limit_per_user_per_day) {
            this.sliderMaxTickets = this.product.ticket_limit_per_user_per_day;
          } else if (this.product.ticket_limit_per_user) {
            this.sliderMaxTickets = this.product.ticket_limit_per_user;
          }

          if (this.product.faq_group) {
            this.product.faq_group.items.forEach((item: FaqItem) => {
              item.answer = this.updateTextPlaceholders(item.answer);
            });
          }
          this.faqItems = this.faqList;
          this.userEngage.productEventByProduct('view', response);
          this.connectToCart();
          this.initDiscountTimer();

          this.setMetaTags(this.product);

          const firstOffer = this.product.offers[0];

          if (firstOffer) {
            this.gtmService.itemView(
              this.product.title,
              String(this.product.id),
              parseFloat(firstOffer.price),
              firstOffer.currency
            );
          }
          this.videoUrl = this.product.video?.url;
          this.videoId = this.product.video?.id;
        },
        error: error => {
          console.warn(error);
          this.isLoading = false;
          if (error && error.status === 404) {
            this.hasError404 = true;
          }
        }
      });
  }

  listenProductItemChange(): void {
    this.formGroup
      .get('tickets')
      .valueChanges.pipe(takeUntil(this._alive$))
      .subscribe({
        next: ticketQuantity => {
          this.sliderTicketQuantity = ticketQuantity;
          this.checkProductAvailability();
        }
      });
  }

  connectToCart(): void {
    this.cart$.pipe(takeUntil(this._alive$)).subscribe({
      next: cartState => {
        if (!cartState.cart) {
          return;
        }

        const cartItems = cartState.cart.items;

        this.cartItemQuantity = cartItems.reduce((sum, item) => {
          if (Number(item.type_id1) === Number(this.product.id)) {
            return sum + item.quantity;
          }
          return sum;
        }, 0);

        this.checkProductAvailability();
      }
    });
  }

  checkProductAvailability(): void {
    this.isTooMuchTicketPerUser =
      this.cartItemQuantity + this.sliderTicketQuantity > this.sliderMaxTickets;
  }

  @HostListener('window:resize')
  onResize() {
    if (window.innerWidth < 1300) {
      this.shareOptions = ['messenger', 'whatsapp', 'copy'];
    } else {
      this.shareOptions = ['facebook', 'whatsapp', 'copy'];
    }
  }

  updateTextPlaceholders(text: string): string {
    text = text.replace(
      /\{draw_date\}/g,
      DateFormatterService.parseAndFormat(this.product.result_at, 'dd/MM/yyyy')
    );
    text = text.replace(
      /\{draw_time\}/g,
      DateFormatterService.parseAndFormat(this.product.result_at, 'HH:mm')
    );

    text = text.replace(/\{total_entries\}/g, String(this.product.max_entries));

    text = this.udpateTextInstantWin(
      text,
      'instant win',
      ProductPageData[this.product.instant_win_type]?.faqLabel
    );

    return text;
  }

  public chooseEntryOption(entryOption: 'online' | 'free' | any): void {
    this.activeEntryOption = entryOption;
  }

  public installApp(): void {
    if (this.projectCode === 'ukcarp' && !GonativeService.isIos()) {
      this.router.navigate(['/install-app-for-android']);
    } else if (GonativeService.isAndroid()) {
      window.location.href = GonativeService.getAndroidAppLink();
    } else if (GonativeService.isIos()) {
      window.location.href = GonativeService.getiOSAppLink();
    } else {
      this.openInstallAppDialog();
    }
  }

  public openBrowser(): void {
    let currentUrl = window.location.href;
    currentUrl +=
      (currentUrl.indexOf('?') > -1 ? '&' : '?') + 'openbrowser=true'; // the rule in GoNative by "openbrowser=true" string
    window.location.href = currentUrl;
  }

  slideRangeInput({ value }): void {
    this.sliderTicketQuantity = value;
  }

  handleSubmit(): void {
    this.formGroup.markAllAsTouched();
    this.triedSubmitTickets = true;

    if (this.formGroup.valid) {
      this._calculateScrollbarWidth();
      this.isAddingToCart = true;
      this.errorMessage = null;
      this.errorMessageTop = null;
      this.addedTickets = this.formGroup.value.tickets;

      this.cartService
        .addDrawItem(
          this.product.id,
          this.addedTickets,
          null,
          null,
          this.queryParams && this.queryParams.from
            ? this.queryParams.from
            : 'generic',
          this.handleSubmitIterations
        )
        .subscribe({
          next: (cart: Cart) => {
            const cartItem: CartItem | null = cart.items.find(
              item => item.type_id1 === this.product.id.toString()
            );
            const offer = this.product.offers[0];

            this.gtmService.addedToCart(
              'cartitem.' + cartItem
                ? cartItem.id
                : Date.now().toString() + Math.random(),
              this.product.title,
              String(this.product.id),
              parseFloat(offer.price) * this.addedTickets,
              offer.currency,
              this.addedTickets
            );

            this.isAddingToCart = false;
            this.isShowSuccessMessage = true;
            this.window.scrollTo(0, 0);
            this.formGroup
              .get('tickets')
              .setValue(this.product.default_tickets);

            this.openProductDialog();

            this.userEngage.productEventByProduct('add to cart', this.product);
            this.triedSubmitTickets = false;
          },
          error: err => {
            if (ErrorProcessorService.isSafePalyErrorResponse(err)) {
              const element = document.getElementById('header-placeholder');
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
              setTimeout(() => {
                this.headerAlertData = {
                  message: err.error.string,
                  link: '/my-account/take-control-of-your-play',
                  type: 'error'
                };
              }, 100);
              this.window.scrollTo(0, 0);
              this.isAddingToCart = false;
            } else {
              if (err !== 'resend') {
                this.errorMessage = this.udpateTextInstantWin(
                  ErrorProcessorService.getErrorFromErrorResponse(err),
                  'tickets',
                  ProductPageData[this.product.instant_win_type]
                    ?.productErrorMessage
                );

                this.errorMessageTop = this.udpateTextInstantWin(
                  ErrorProcessorService.getErrorFromErrorResponse(err),
                  'tickets',
                  ProductPageData[this.product.instant_win_type]
                    ?.productErrorMessage
                );
                this.isShowSuccessMessage = false;
                this.window.scrollTo(0, 0);
                this.isAddingToCart = false;
              } else {
                this.handleSubmitIterations++;
                this.handleSubmit();
              }
            }
          }
        });
    }

    if (!this.formGroup.valid) {
      this.errorMessage = 'Please answer the question';
      this.isShowSuccessMessage = false;
    }
  }

  getInstantWinGroups(slug): void {
    this.httpClient.get(API_LIST.getInstantWinGroupBySlug(slug)).subscribe({
      next: (response: InstantWinGroup[]) => {
        if (response.length > 0) {
          this.instantWinGroup = response;
        }
        this.isLoadingInstantWinGroup = false;
      }
    });
  }

  handleIncrement(): void {
    const value = this.formGroup.value.tickets;

    if (value < this.sliderMaxTickets) {
      this.formGroup.get('tickets').setValue(value + 1);
    }
  }

  handleDecrement(): void {
    const value = this.formGroup.value.tickets;

    if (value > this.min) {
      this.formGroup.get('tickets').setValue(value - 1);
    }
  }

  openWorldwideDialog(): void {
    this.dialog.open(DialogWorldwideComponent, {
      backdropClass: 'app-overlay-backdrop',
      panelClass: 'worldwide-dialog'
    });
  }

  openAutodrawDialog(): void {
    this.dialog.open(AutodrawDialogComponent, {
      backdropClass: 'app-overlay-backdrop',
      panelClass: 'worldwide-dialog'
    });
  }

  openInstallAppDialog(): void {
    this.dialog.open(InstallAppDialogComponent, {
      backdropClass: 'app-overlay-backdrop',
      panelClass: 'install-app-dialog'
    });
  }

  openProductDialog(): void {
    this.dialog.open(ProductDialogComponent, {
      backdropClass: 'app-overlay-backdrop',
      panelClass: 'product-dialog',
      data: {
        product: this.product,
        tickets: this.addedTickets,
        projectCode: this.projectCode,
        text: this.productPageText.productDialogText
      }
    });
  }

  getQueryParams(): void {
    this.route.queryParams.pipe(takeUntil(this._alive$)).subscribe({
      next: params => {
        this.queryParams = params;
      }
    });
  }

  installPageScrollListener() {
    fromEvent(window, 'scroll')
      .pipe(delay(500), takeUntil(this._alive$))
      .subscribe(() => {
        const domEL = document.getElementById('instant-win-section');

        if (domEL && this.isActiveLessBtn) {
          const rect = domEL.getBoundingClientRect();
          this.showLessInstantWinGroup =
            rect.height - window.screen.height + rect.top <= 0;
          this.stickyLessInstantWinGroup =
            rect.top - window.screen.height + 400 <= 0 &&
            !this.showLessInstantWinGroup;
        }
      });
  }

  public showLessBtn(): void {
    this.instantWinGroupLimit = this.instantWinGroup.length;
    this.isActiveLessBtn = true;
    this.stickyLessInstantWinGroup = true;
  }

  public hideLessBtn(): void {
    this.instantWinGroupLimit = this.groupLimit;
    this.isActiveLessBtn = false;
    this.stickyLessInstantWinGroup = false;
    this.closeAllCard = true;

    setTimeout(() => {
      this.closeAllCard = false;
      const domEL = document.getElementById('how-more-btn');
      if (domEL) {
        const rect = domEL.getBoundingClientRect();

        this.window.scrollTo({
          top: rect.top + window.pageYOffset + rect.height - 300,
          behavior: 'smooth'
        });
      }
    }, 300);
  }

  handleItemNavClick() {
    const top =
      document.getElementById('product-tabs-section').offsetTop -
      document.querySelector('.header-container')['offsetHeight'] -
      document.getElementById('mobile-sticky-bar').offsetHeight / 2 -
      10;

    window.scrollTo({ top, behavior: 'smooth' });
  }

  public onExpired(bool) {
    this.showCountdown = !bool;
  }

  private onWindowScroll(): void {
    if (this.mobileStickyTriggerElement) {
      const headerHeight =
        document.querySelector('.header-container')['offsetHeight'];
      const triggerPosition =
        this.mobileStickyTriggerElement.nativeElement.offsetTop +
        this.mobileStickyTriggerElement.nativeElement.offsetHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;

      this.zone.run(() => {
        this.showMobileStickyBar = scrollTop >= triggerPosition - headerHeight;
      });
    }
  }

  private _calculateScrollbarWidth(): void {
    let scrollBarWidth = 0;
    const root = this.document.documentElement;
    const hasScrollbar = window['innerWidth'] > this.document.body.clientWidth;
    if (hasScrollbar) {
      const scrollEl = this.scrollbarMeasure.nativeElement;
      scrollBarWidth = scrollEl.offsetWidth - scrollEl.clientWidth;
    }

    root.style.setProperty('--scrollbar-width', scrollBarWidth + 'px');
  }

  private resetCompetitionData() {
    this.product = undefined;
    this.faqItems = undefined;
    this.min = 1;
    this.errorMessage = null;
    this.errorMessageTop = null;
    this.isShowSuccessMessage = false;
    this.isLoading = true;
    this.message = '';
    this.entranceAvailable = true;
    this.installAppMode = false;
    this.openBrowserMode = false;
    this.sliderTicketQuantity = null;
    this.cartItemQuantity = 0;
    this.sliderMaxTickets = 100;
    this.isTooMuchTicketPerUser = null;
    this.formGroup = this.formBuilder.group({
      tickets: [1]
    });
  }

  private udpateTextInstantWin(value: string, from: string, to: string) {
    if (
      this.isInstantWin &&
      this.product.instant_win_type !== RaffleInstantType.Regular
    ) {
      const regExp = new RegExp(`\\b${from}\\b`, 'gi');
      return value.replace(regExp, to);
    }
    return value;
  }
}
